'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  ICategory,
  ICategoryOption,
  ICategoryWithMasterclasses,
} from '@/entities/categories/types';
import { IMasterclass } from '@/entities/masterclass/types';
import { queryClient } from '@/shared/api/query-client';
import { QUERY_KEYS } from '@/shared/const/query-keys';
import { useSession } from '@/shared/hooks/use-session';
import { getApiUrl } from '@/shared/utils/getApiUrl';
import { useToast } from '@/components/ui/use-toast';
/**
 * Хук для получения всех категорий
 */
export const useGetAllCategoriesQuery = () => {
  const { isPending, token } = useSession();
  return useQuery({
    queryKey: [QUERY_KEYS.categories],
    queryFn: async () => {
      return await axios.get(
        getApiUrl(`/categories`),
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      );
    },
    select: (data): ICategory[] => {
      return data.data.data;
    },
    enabled: !isPending,
  });
};

export type UseGetCategoriesAsOptionsQueryOptions = {
  /** Снимок с SSR — табы категорий без ожидания клиента */
  initialCategoryOptions?: ICategoryOption[];
};

/**
 * Хук для получения категорий в формате для селектов
 */
export const useGetCategoriesAsOptionsQuery = (
  options?: UseGetCategoriesAsOptionsQueryOptions,
) => {
  const { isPending, token } = useSession();
  return useQuery({
    queryKey: [QUERY_KEYS.categories, 'options'],
    queryFn: async (): Promise<ICategoryOption[]> => {
      const res = await axios.get(
        getApiUrl(`/category-options`),
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      );
      return res.data as ICategoryOption[];
    },
    placeholderData: options?.initialCategoryOptions,
    enabled: !isPending,
  });
};

/**
 * Хук для получения категории по ID
 */
export const useGetCategoryByIdQuery = (id: string | number) => {
  const { isPending, token } = useSession();
  return useQuery({
    queryKey: [QUERY_KEYS.categories, id],
    queryFn: async () => {
      return await axios.get(
        getApiUrl(`/categories/${id}`),
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      );
    },
    select: (data): ICategory => data.data,
    enabled: !isPending && !!id,
  });
};

/**
 * Хук для получения мастер-классов по ID категории
 */
export const useGetMasterclassesByCategoryIdQuery = (
  categoryId: string | number,
) => {
  const { isPending, token } = useSession();
  return useQuery({
    queryKey: [QUERY_KEYS.masterclasses, 'byCategory', categoryId],
    queryFn: async () => {
      return await axios.get(
        getApiUrl(`/masterclasses/by-category/${categoryId}`),
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      );
    },
    select: (data): IMasterclass[] => {
      return data.data.results;
    },
    enabled: !isPending && !!categoryId,
  });
};

/**
 * Хук для получения мастер-классов по значению value категории
 */
export const useGetMasterclassesByCategoryValueQuery = (
  categoryValue: string,
) => {
  const { isPending, token } = useSession();
  return useQuery({
    queryKey: [QUERY_KEYS.masterclasses, 'byCategoryValue', categoryValue],
    queryFn: async () => {
      return await axios.get(
        getApiUrl(`/masterclasses/by-category-value/${categoryValue}`),
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      );
    },
    select: (data): IMasterclass[] => {
      return data.data.results;
    },
    enabled: !isPending && !!categoryValue,
  });
};

/**
 * Хук для получения категории с мастер-классами
 */
export const useGetCategoryWithMasterclassesQuery = (
  categoryId: string | number,
) => {
  const { isPending, token } = useSession();
  return useQuery({
    queryKey: [QUERY_KEYS.categories, categoryId, 'withMasterclasses'],
    queryFn: async () => {
      return await axios.get(
        getApiUrl(`/categories/${categoryId}/masterclasses`),
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      );
    },
    select: (data): ICategoryWithMasterclasses => data.data,
    enabled: !isPending && !!categoryId,
  });
};

/**
 * Хук для обновления порядка категорий
 */
export const useUpdateCategoriesOrderMutation = () => {
  const { token } = useSession();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (categories: Pick<ICategory, 'id' | 'order'>[]) => {
      return await axios.put(
        getApiUrl(`/categories/update-order`),
        {
          categories,
        },
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      );
    },
    onSuccess: () => {
      toast({
        title: 'Порядок категорий обновлен',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.categories] });
    },
  });
};
